import React, { useId, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface BulkDeleteDialogProps {
  open: boolean;
  names: string[];
  onConfirm: () => void;
  onCancel: () => void;
}

export const BulkDeleteDialog: React.FC<BulkDeleteDialogProps> = ({
  open,
  names,
  onConfirm,
  onCancel,
}) => {
  const n = names.length;
  const noun = n === 1 ? 'connector' : 'connectors';
  const phrase = `delete ${n} ${noun}`;
  const inputId = useId();

  const [typed, setTyped] = useState('');

  // Reset the typed confirmation text when the dialog closes. Adjust state
  // during render (https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes)
  // via a previous-value mirror instead of an effect: `prevOpen` converges to
  // `open` on the very next render, so this fires exactly once per close.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (!open) setTyped('');
  }

  const armed = typed === phrase;

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{`Delete ${n} ${noun}?`}</AlertDialogTitle>
          <AlertDialogDescription>
            This cannot be undone. The {noun} and their stored credentials will be permanently removed:
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="max-h-48 list-disc overflow-y-auto rounded-md border bg-muted/30 p-2 pl-6 text-sm">
          {names.map((nm) => (
            <li key={nm} className="py-0.5">{nm}</li>
          ))}
        </ul>
        <div className="space-y-1">
          <Label htmlFor={inputId}>
            {`Type "${phrase}" to confirm:`}
          </Label>
          <Input
            id={inputId}
            aria-label="type-to-confirm"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoFocus
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={!armed}
            onClick={armed ? onConfirm : undefined}
            className={cn(buttonVariants({ variant: 'destructive' }), !armed && 'opacity-50 cursor-not-allowed')}
          >
            {`Delete ${n} ${noun}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
